import React, { useState, useEffect } from 'react';
import { 
    Box, Card, CardHeader, Button, Typography, CircularProgress,
    Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import { Key as KeyIcon } from '@mui/icons-material';
import { regenerateUserAPIKey, getCurrentUser, checkAdminKey } from './api';

function AdminKeyPanel({ apiKey, onError }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [keyInfo, setKeyInfo] = useState(null);
    const [openKeyDialog, setOpenKeyDialog] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [currentUserId, setCurrentUserId] = useState(null);
    
    // Fetch current user ID on mount
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const user = await getCurrentUser(apiKey);
                setCurrentUserId(user.id);
            } catch (err) {
                console.error("Failed to fetch current user:", err);
                const errorMsg = err.data?.error || err.message || "Failed to fetch current user";
                setError(`Error: ${errorMsg}`);
                if(onError) onError(errorMsg);
            }
        };
        fetchCurrentUser();
    }, [apiKey, onError]);
    
    // Fetch current key details
    const handleCheckKey = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await checkAdminKey(apiKey);
            setKeyInfo(result);
            if(onError) onError(null);
        } catch (err) {
            console.error("Failed to check admin key:", err);
            const errorMsg = err.data?.error || err.message || "Failed to check admin key";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
            if(onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    
    // Regenerate user API key
    const handleRegenerateKey = async () => {
        if (!currentUserId) {
            setError('Error: User ID not available. Please refresh the page.');
            return;
        }
        
        if (!window.confirm('Are you sure you want to regenerate your API key? You will need to use the new key to authenticate after this operation.')) return;
        
        setLoading(true);
        setError('');
        try {
            const result = await regenerateUserAPIKey(apiKey, currentUserId);
            setNewKey(result.apiKey);
            setOpenKeyDialog(true);
            if(onError) onError(null);
        } catch (err) {
            console.error("Failed to regenerate user API key:", err);
            const errorMsg = err.data?.error || err.message || "Failed to regenerate user API key";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
            if(onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <Card elevation={2} sx={{ overflow: 'hidden', mt: 3 }}>
            <CardHeader
                title={<Typography variant="h6">Admin Key Management</Typography>}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<KeyIcon />}
                            onClick={handleRegenerateKey}
                            disabled={loading}
                        >
                            Regenerate Key
                        </Button>
                    </Box>
                }
            />
            
            <Box sx={{ px: 2, pb: 2 }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                <Alert severity="info" sx={{ mb: 2 }}>
                    This panel allows you to manage your API key. Regenerating the key will invalidate your current key.
                </Alert>
                
                {keyInfo && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Your API key is valid.
                    </Alert>
                )}
            </Box>
            
            {/* New Key Dialog */}
            <Dialog 
                open={openKeyDialog} 
                onClose={() => setOpenKeyDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>New API Key Generated</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Save this API key now! It will not be displayed again.
                    </Alert>
                    <TextField
                        fullWidth
                        value={newKey}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="filled"
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            navigator.clipboard.writeText(newKey);
                            alert('API key copied to clipboard');
                        }} 
                        color="primary"
                    >
                        Copy to Clipboard
                    </Button>
                    <Button onClick={() => setOpenKeyDialog(false)} variant="contained" color="primary">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default AdminKeyPanel;